import { useAdmins, Admin, AdminType } from '../../hooks/registrants/useAdmins';
import { useEntityAdmins, EntityAdmin } from '../../hooks/users/useEntityAdmins';

export type AdminData = Admin | EntityAdmin;

export interface AdminFilterState {
  locationId: string | null;
  unitId: string;
  contractId: string;
  locationName?: string;
  unitName?: string;
  contractName?: string;
}

export interface AdminQueryParams {
  searchQuery: string;
  page: number;
  limit: number;
}

export interface AdminResult {
  admins: AdminData[];
  totalPages: number;
  isLoading: boolean;
  adminType: string;
}

export function useAdminData(
  filters: AdminFilterState,
  queryParams: AdminQueryParams,
): AdminResult {
  const { searchQuery, page, limit } = queryParams;

  const locationAdmins = useEntityAdmins({
    entityId: filters.locationId || '',
    entityType: 'location',
    searchQuery,
    page,
    limit,
  });

  const unitAdmins = useEntityAdmins({
    entityId: filters.unitId || '',
    entityType: 'unit',
    searchQuery,
    page,
    limit,
  });

  const contractAdmins = useEntityAdmins({
    entityId: filters.contractId || '',
    entityType: 'contract',
    searchQuery,
    page,
    limit,
  });

  const adminType: AdminType = 'global';
  const globalAdmins = useAdmins({
    type: adminType,
    searchQuery,
    page,
    limit,
  });

  if (filters.locationId) {
    return {
      admins: locationAdmins.data?.admins || [],
      totalPages: locationAdmins.data?.totalPages || 1,
      isLoading: locationAdmins.isLoading,
      adminType: 'location',
    };
  }

  if (filters.unitId) {
    return {
      admins: unitAdmins.data?.admins || [],
      totalPages: unitAdmins.data?.totalPages || 1,
      isLoading: unitAdmins.isLoading,
      adminType: 'unit',
    };
  }

  if (filters.contractId) {
    return {
      admins: contractAdmins.data?.admins || [],
      totalPages: contractAdmins.data?.totalPages || 1,
      isLoading: contractAdmins.isLoading,
      adminType: 'contract',
    };
  }

  return {
    admins: globalAdmins.data?.admins || [],
    totalPages: globalAdmins.data?.totalPages || 1,
    isLoading: globalAdmins.isLoading,
    adminType: 'global',
  };
}

export function getAdminTitle(filters: AdminFilterState): string {
  if (filters.locationId) return 'Location Admins';
  if (filters.contractId) return 'Contract Admins';
  if (filters.unitId) return 'Unit Admins';
  return 'Global Admins';
}

export function getFilterDetail(filters: AdminFilterState): string {
  if (filters.locationId) {
    return filters.locationName
      ? `for ${filters.locationName}`
      : `for Location ID: ${filters.locationId}`;
  }
  if (filters.contractId) {
    return filters.contractName
      ? `for ${filters.contractName}`
      : `for Contract ID: ${filters.contractId}`;
  }
  if (filters.unitId) {
    return filters.unitName ? `for ${filters.unitName}` : `for Unit ID: ${filters.unitId}`;
  }
  return '';
}
